import { useCallback, useEffect, useRef, useState } from 'react'
import { Send, X, Loader2 } from 'lucide-react'

import { beginTelegramLink, getTelegramLinkStatus } from '../api/telegramLink'

/**
 * TelegramLinkPrompt — non-intrusive nudge to connect the Telegram bot.
 *
 * Why: важные уведомления (high/critical) уходят в Telegram только тем, у кого
 * привязан аккаунт. Привязка живёт в Личном кабинете, но охват низкий — этот
 * баннер доводит остальных до `/telegram-link/begin` без давления.
 *
 * Appears once per 14 days when ALL of:
 *   - пользователь ещё НЕ привязал Telegram (getTelegramLinkStatus → linked:false)
 *   - баннер не закрыт в пределах окна dismiss
 *
 * Show delay = 50s — пропускаем вперёд InstallPrompt (8s) и web-push prompt (30s),
 * чтобы не показывать три карточки разом. z-index ниже их обоих.
 *
 * On accept → beginTelegramLink() открывает t.me/<bot>?start=link_<token>,
 * затем поллим статус каждые 3с до linked:true (как TelegramLinkCard в кабинете).
 */

const DISMISS_KEY = 'onboardix_tg_link_prompt_dismissed_until'
const DISMISS_DAYS = 14
const SHOW_DELAY_MS = 50_000
const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 10 * 60 * 1000 // токен привязки живёт 10 минут

function isDismissed() {
    try {
        const until = Number(localStorage.getItem(DISMISS_KEY) || '0')
        return until && Date.now() < until
    } catch {
        return false
    }
}

export default function TelegramLinkPrompt() {
    const [visible, setVisible] = useState(false)
    const [busy, setBusy] = useState(false)
    const [polling, setPolling] = useState(false)
    const pollRef = useRef(null)

    useEffect(() => {
        let cancelled = false
        if (isDismissed()) return undefined

        const t = setTimeout(async () => {
            if (cancelled) return
            try {
                const s = await getTelegramLinkStatus()
                if (cancelled) return
                // Уже привязан — показывать нечего.
                if (s?.linked) return
                setVisible(true)
            } catch {
                // Статус не получили (сеть / эндпоинт недоступен) — молча не показываем.
            }
        }, SHOW_DELAY_MS)

        return () => {
            cancelled = true
            clearTimeout(t)
        }
    }, [])

    // Чистим интервал поллинга на анмаунте.
    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

    const startPolling = useCallback(() => {
        setPolling(true)
        const started = Date.now()
        pollRef.current = setInterval(async () => {
            if (Date.now() - started > POLL_TIMEOUT_MS) {
                clearInterval(pollRef.current)
                pollRef.current = null
                setPolling(false)
                return
            }
            try {
                const s = await getTelegramLinkStatus()
                if (s?.linked) {
                    clearInterval(pollRef.current)
                    pollRef.current = null
                    setPolling(false)
                    setVisible(false)
                }
            } catch { /* keep polling */ }
        }, POLL_INTERVAL_MS)
    }, [])

    const handleConnect = useCallback(async () => {
        setBusy(true)
        try {
            const { deep_link } = await beginTelegramLink()
            if (!deep_link) throw new Error('no-deep-link')
            window.open(deep_link, '_blank', 'noopener,noreferrer')
            startPolling()
        } catch (err) {
            if (typeof window !== 'undefined' && window.console) {
                console.warn('[telegram-link] begin failed:', err?.message || err)
            }
        } finally {
            setBusy(false)
        }
    }, [startPolling])

    const handleDismiss = useCallback(() => {
        try {
            const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000
            localStorage.setItem(DISMISS_KEY, String(until))
        } catch { /* noop */ }
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
        setVisible(false)
    }, [])

    if (!visible) return null

    const cardStyle = {
        position: 'fixed',
        left: '50%',
        bottom: 'max(20px, env(safe-area-inset-bottom))',
        transform: 'translateX(-50%)',
        zIndex: 99996, // под InstallPrompt (99998) и web-push prompt (99997)
        background: 'color-mix(in srgb, var(--color-shell-surface) 96%, transparent)',
        color: 'var(--color-shell-text)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '14px 18px',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.875rem',
        maxWidth: 'min(440px, calc(100vw - 24px))',
        border: '1px solid rgba(255,255,255,0.08)',
        animation: 'onboardix-pwa-slideup 0.35s ease',
    }
    const btnPrimary = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 14px',
        borderRadius: 'var(--radius-md)',
        border: 'none',
        background: 'var(--color-accent)',
        color: 'var(--color-text-inverse)',
        fontWeight: 600,
        fontSize: '0.812rem',
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.7 : 1,
        whiteSpace: 'nowrap',
    }
    const btnDismiss = {
        background: 'transparent',
        border: 'none',
        color: 'var(--color-text-muted)',
        cursor: 'pointer',
        padding: 4,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
    }

    return (
        <div role="dialog" aria-label="Подключить Telegram" style={cardStyle}>
            <Send size={22} color='var(--color-accent)' style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, lineHeight: 1.4 }}>
                {polling
                    ? 'Откройте бот и нажмите «Запустить» — как только подтвердите, окно закроется.'
                    : 'Подключите Telegram — будем присылать уведомления о задачах, откликах и важных событиях прямо в мессенджер.'}
            </div>
            <button type="button" style={btnPrimary} onClick={handleConnect} disabled={busy || polling}>
                {polling
                    ? <><Loader2 size={14} className="spin" /> Ждём…</>
                    : (busy ? 'Открываем…' : 'Подключить')}
            </button>
            <button
                type="button"
                style={btnDismiss}
                onClick={handleDismiss}
                aria-label="Закрыть"
                title="Не показывать 14 дней"
            >
                <X size={18} />
            </button>
        </div>
    )
}
